import { Fragment } from 'react';
import type { PackBucketRow, PackBucketsBucket } from '../../lib/api/operatorConsole';
import { formatPercent } from '../MyPacks/packBuilderFormat';
import { formatDollars } from './pricing';

type Props = {
  groups: PackBucketRow[];
  buckets: PackBucketsBucket[];
  price: number;
};

type Segment = {
  range: string;
  share: number | null;
  stocked: boolean;
  side: PackBucketsBucket['side'];
};

const SIDE_FILL: Record<PackBucketsBucket['side'], string> = {
  low: 'bg-[var(--co-muted)] opacity-70',
  high: 'bg-[var(--co-success)]',
};

function rangeLabel(group: PackBucketRow | undefined, fallback: string): string {
  if (!group) return fallback;
  const from = formatDollars(group.from);
  return group.to == null ? `${from}+` : `${from}–${formatDollars(group.to)}`;
}

function shareLabel(segment: Segment): string {
  return segment.stocked ? formatPercent(segment.share, 1) : 'empty';
}

function widthOf(share: number | null): string {
  return `${Math.round((share ?? 0) * 10000) / 100}%`;
}

function PriceMark({ price }: { price: number }) {
  return (
    <span aria-hidden="true" className="relative w-0 shrink-0 self-stretch">
      <span className="absolute -inset-y-1 left-0 w-px bg-[var(--co-text)]" />
      <span className="absolute bottom-full left-0 mb-1.5 -translate-x-1/2 whitespace-nowrap text-[10px] leading-none text-[var(--co-text)]">
        price {formatDollars(price)}
      </span>
    </span>
  );
}

function Bar({ label, segments, price }: { label: string; segments: Segment[]; price: number }) {
  const firstHigh = segments.findIndex((s) => s.side === 'high');
  const marked = firstHigh > 0;
  const described = segments.map((s) => `${s.range} ${shareLabel(s)}`).join(', ');
  return (
    <div className={`flex items-center gap-3 ${marked ? 'pt-4' : ''}`}>
      <span className="w-32 shrink-0 text-[11px] text-[var(--co-muted)]">{label}</span>
      <div role="img" aria-label={`${label}: ${described}`} className="flex h-3 flex-1 gap-px">
        {segments.map((segment, index) => (
          <Fragment key={index}>
            {marked && index === firstHigh && <PriceMark price={price} />}
            <span
              title={`${segment.range} · ${shareLabel(segment)}`}
              className={`h-full min-w-[3px] first:rounded-l last:rounded-r ${SIDE_FILL[segment.side]}`}
              style={{ width: widthOf(segment.share) }}
            />
          </Fragment>
        ))}
      </div>
    </div>
  );
}

/** Each bucket's share of pulls as one strip per odds set, split at the pack price. */
export function BucketShareBars({ groups, buckets, price }: Props) {
  const segmentsBy = (share: (bucket: PackBucketsBucket) => number | null): Segment[] =>
    buckets.map((bucket, index) => ({
      range: rangeLabel(groups[index], bucket.range),
      share: share(bucket),
      stocked: bucket.stocked,
      side: bucket.side,
    }));
  const yourOdds = buckets.some((b) => b.your_share != null);

  return (
    <div className="flex flex-col gap-2">
      <Bar label="Pack odds" segments={segmentsBy((b) => b.share)} price={price} />
      {yourOdds && <Bar label="Your Normal odds" segments={segmentsBy((b) => b.your_share)} price={price} />}
      <p className="flex items-center gap-4 whitespace-nowrap text-[11px] text-[var(--co-muted)]">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className={`h-2 w-3 rounded-sm ${SIDE_FILL.low}`} />
          below the price
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className={`h-2 w-3 rounded-sm ${SIDE_FILL.high}`} />
          at or above the price
        </span>
      </p>
    </div>
  );
}
